import {
    UseInfiniteQueryResult,
    UseMutationResult,
    UseQueryResult,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    InfiniteData
} from '@tanstack/react-query'
import { useMe } from '../../../shared/hooks/useMe'
import { Company } from '../../../shared/models/companyModel'
import { QueryParams } from '../../../shared/models/queryModels'
import { RequestResponse } from '../../../shared/services/requestServices'
import {
    CreateSubscription,
    DeleteSubscription,
    EditSubscription,
    Subscription
} from '../../../shared/models/subscriptionModels'
import { mapQuery } from '../../../shared/utils/queryUtils'
import {
    createSubscriptionService,
    deleteSubscriptionService,
    editSubscriptionService,
    getSubscriptionByIdService,
    listSubscriptionsService
} from '../../../shared/services/subscriptionsServices'
import { useQueryParamsState } from '../../../shared/hooks/useQueryParams'
import { resolveSubscriptionFilters } from '../utils/subscriptionUtils'
import { subscriptionTableFields } from '../enums/subscriptionEnums'
import { deprecated_useSetToast } from '../../../shared/hooks/useSetToast'

const subscriptionsQueryKeys = {
    root: ['infiniteSubscriptions'],
    infiniteSubscriptions: (
        companyId: Company['id'],
        queryParams?: QueryParams
    ): (string | QueryParams | undefined)[] => [...subscriptionsQueryKeys.root, companyId, queryParams],
    subscriptionById: (companyId: Company['id'], subscriptionId: string | undefined): (string | undefined)[] => [
        ...subscriptionsQueryKeys.root,
        companyId,
        subscriptionId
    ]
}

export const useInfiniteSubscriptions = (): UseInfiniteQueryResult<
    InfiniteData<RequestResponse<Subscription[]> | undefined>
> => {
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()

    return useInfiniteQuery({
        queryKey: subscriptionsQueryKeys.infiniteSubscriptions(
            companyId,
            queryParamsState.pick([`subscriptions_search`, `subscriptions_sort`, `subscriptions_filter`])
        ),

        queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
            const mappedQuery = mapQuery({
                filter: resolveSubscriptionFilters({
                    customFilters: queryParamsState.get(`subscriptions_filter`)
                }),
                search: {
                    value: queryParamsState.get(`subscriptions_search`),
                    path: subscriptionTableFields
                },
                sortBy: queryParamsState.get(`subscriptions_sort`),
                pagination: { after: pageParam }
            })

            return listSubscriptionsService(companyId, mappedQuery)
        },

        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

export const useSubscriptionById = (
    subscriptionId: string | undefined
): UseQueryResult<RequestResponse<Subscription> | undefined> => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: subscriptionsQueryKeys.subscriptionById(companyId, subscriptionId),
        queryFn: () => getSubscriptionByIdService(companyId, subscriptionId)
    })
}

export const useCreateSubscription = (): UseMutationResult<
    RequestResponse<Subscription> | undefined,
    unknown,
    CreateSubscription
> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const queryParams = useQueryParamsState()

    return useMutation({
        mutationFn: (payload: CreateSubscription) => createSubscriptionService(companyId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKeys.infiniteSubscriptions(
                    companyId,
                    queryParams.pick([`subscriptions_search`, `subscriptions_sort`, `subscriptions_filter`])
                )
            })
        }
    })
}

export const useUpdateSubscription = (): UseMutationResult<
    RequestResponse<Subscription> | undefined,
    unknown,
    EditSubscription
> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()
    const queryParams = useQueryParamsState()

    return useMutation({
        mutationFn: (payload: EditSubscription) => editSubscriptionService(companyId, payload),
        onSuccess: (response) => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKeys.subscriptionById(companyId, response?.body?.id)
            })
            queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKeys.infiniteSubscriptions(
                    companyId,
                    queryParams.pick([`subscriptions_search`, `subscriptions_sort`, `subscriptions_filter`])
                )
            })
        }
    })
}

export const useDeleteSubscription = (): UseMutationResult<
    RequestResponse<Subscription> | undefined,
    unknown,
    DeleteSubscription
> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()
    const setToast = deprecated_useSetToast()

    const queryParams = useQueryParamsState()

    return useMutation({
        mutationFn: (payload: DeleteSubscription) => deleteSubscriptionService(companyId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKeys.infiniteSubscriptions(
                    companyId,
                    queryParams.pick([`subscriptions_search`, `subscriptions_sort`, `subscriptions_filter`])
                )
            })
            setToast('deleteSubscription', 'positive')
        },
        onError: () => setToast('deleteSubscription', 'negative')
    })
}
